/* eslint-disable react-hooks/exhaustive-deps */
import styles from './Header.module.css';
import React, { useEffect, useState } from 'react';
import {
    TextField,
    Button,
    Tooltip,
    Avatar,
    IconButton,
    Menu,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { Link, useLocation  } from 'react-router-dom';
import { useAuth } from '../../../../auth/auth';
import { removeUser } from '../../../../auth/actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { generatePayrollReport } from '../../../../api/apiCall';
import { getInitials, getRandomColor } from '../../../../utils/utils';

type Props = {
    setDate: React.Dispatch<React.SetStateAction<any>>
    week: number
    setWeek: React.Dispatch<React.SetStateAction<number>>
    year: number
    setYear: React.Dispatch<React.SetStateAction<number>>
    setDisabledCells?: any
    updateParams?: any
};

const Header = ({ setDate, week, setWeek, year, setYear, setDisabledCells, updateParams }: Props) => {
    const {state, dispatch} = useAuth();
    const [avatarColor, setAvatarColor] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [anchors, setAnchors] = useState<{[key: string]: HTMLElement | null}>({
      user: null,
      staff: null,
      rtdata: null,
      sentinel: null,
      oncall: null,
      resport: null,
      mobile: null,
    });

    const handleOpen = (menu: string) => (event: React.MouseEvent<HTMLElement>) => {
        setAnchors((prev) => ({ ...prev, [menu]: event.currentTarget }));
    };

    const handleClose = (menu: string) => () => {
        setAnchors((prev) => ({ ...prev, [menu]: null }));
    };

    const location = useLocation();
    //const isMobile = useMediaQuery('(max-width:1245px)');

    const logOut = () => {
        dispatch(removeUser());
        localStorage.removeItem('avatarColor');
    }

    const getRailwayYearStartDate = (year: number): Date => {
        const aprilFirst = new Date(year, 3, 1);
        const dayOfWeek = aprilFirst.getDay();
        const daysBeforeSaturday = (dayOfWeek + 1) % 7;
        return new Date(year, 2, 31 - daysBeforeSaturday);
    };

    const getWeekStartDate = (week: number, year: number): Date => {
        const railwayYearStartDate = getRailwayYearStartDate(year);
        const startDate = new Date(railwayYearStartDate);
        startDate.setDate(railwayYearStartDate.getDate() + week * 7);
        setDate(startDate);
        return startDate;
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        setLoading(true)
        if (week && year) {
            if (week < 1 || week > 53 || year < 2024) {
                alert('Invalid week or year');
                return;
            }
            setDisabledCells?.([]);
            updateParams?.();
            getWeekStartDate(week, year);
        } else {
            alert('Please fill in both fields');
        }
        setTimeout(() => {
            setLoading(false);
        }, 2000);
    };

    const handleGeneratePayrollReport = async () => {
        await generatePayrollReport(
            state.user?.token,
            week,
            year
        );
    };

    const menuConfig = [
        {
          key: 'staff',
          label: 'Staff',
          items: [
            { label: 'Staff Details', path: '/pace-team/resourcing/staff-details' },
            { label: `Weekly New Users`, path: `/pace-team/resourcing/resource-tracker/weekly-new-users?year=${year}&week=${week}` },
            { label: `Workers Week ${week}`, path: `/pace-team/resourcing/resource-tracker/workers?year=${year}&week=${week}` },
            { label: 'Workforce Map', path: '/pace-team/resourcing/workforce-map' },
            { label: "Cancelled shifts", path: "/pace-team/cancelled-shifts"},
          ],
        },
        {
          key: 'rtdata', 
          label: 'RT Data',
          items: [
            { label: 'Clients', path: '/pace-team/collaborations/clients' },
            { label: 'Projects', path: '/pace-team/collaborations/projects' },
            { label: 'Other Data', path: '/pace-team/rt-data' },
            { label: 'Payment Methods', path: '/pace-team/payroll/payment-method' },
            { label: 'Authorized Drivers', path: '/pace-team/resourcing/authorized-drivers' },
            { label: 'Distance Matrices', path: '/pace-team/resourcing/distance-matrices' },
            {
                label: "Calendar Downloads",
                path: "/pace-team/calendar-downloads",
            },
          ],
        },
        {
          key: 'sentinel',
          label: 'Sentinel',
          items: [
            { label: 'Fatigues', path: `/pace-team/resourcing/resource-tracker/fatigue-check?year=${year}&week=${week}` },
            { label: 'Competencies', path: '/pace-team/competencies' },
            { label: 'Experiences', path: '/pace-team/experiences' },
            { label: 'Tickets', path: '/pace-team/tickets' },
            { label: "FRI Calculations", path: "/pace-team/fatigue-calculations" },
            { label: "Upload Weekly Sheet", path: "/pace-team/upload-weekly-sheet" },
          ],
        },
        {
          key: 'oncall',
          label: 'On Call',
          items: [
            { label: 'On Call', path: `/pace-team/resourcing/resource-tracker/on-call?year=${year}&week=${week}` },
            { label: 'Live Tracker', path: '/pace-team/resourcing/live-tracker' },
            { label: 'Location Logger', path: 'https://railresourcing.pace-is.co.uk/pace-team/resource-tracker/location-logger', external: true },
            { label: 'Signin Sheet', path: '/pace-team/resource-tracker/signin' },
            { label: 'Graphs', path: `/pace-team/resourcing/resource-tracker/graph?year=${year}&week=${week}` },
          ],
        },
        {
          key: 'report',
          label: 'Report',
          items: [
            { label: 'Generate Payroll Report', onClick: handleGeneratePayrollReport },
            { label: 'Generate Colas Timesheets', path: `/pace-team/resourcing/resource-tracker/generate-timesheet?year=${year}&week=${week}` },
            { label: 'Generate Advisory Fatigues', path: `/pace-team/resourcing/generate-fatigues` },
            { label: 'Generate Weekly Hours', path: `/pace-team/resourcing/generate-weekly-hours` },
          ],
        },
        {
          key: 'mobile',
          label: 'Mobile',
          iconColor: 'orangered',
          items: [
            { label: 'Job Listings', path: '/pace-team/resourcing/job-listings' },
            { label: 'Web Forms', path: '/pace-team/resourcing/web-forms' },
            //{ label: 'Workers Availability', path: '/pace-team/resourcing/workers-availability' },
          ],
        },
    ];

    useEffect(() => {
        if (week && year) {
            getWeekStartDate(week, year);
        }

        let storedColor = localStorage.getItem('avatarColor');
        if (!storedColor) {
            storedColor = getRandomColor();
            localStorage.setItem('avatarColor', storedColor);
        }
        setAvatarColor(storedColor);
    }, []);

    return (
        <div className={styles.header}>
            <form onSubmit={handleSubmit}>
                <TextField
                    label="Week"
                    type="number"
                    size="small"
                    value={week}
                    onChange={(e) => setWeek(parseInt(e.target.value, 10))}
                    style={{margin: '15px', width: '148px'}}
                    inputProps={{min: 1, max:  53}}
                />
                <TextField
                    label="Year"
                    type="number"
                    size="small"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value, 10))}
                    style={{margin: '15px', width: '148px'}}
                    inputProps={{min: 2024}}
                />
                <Button
                    type="submit"
                    variant="outlined"
                    disabled={loading}
                    style={{margin: '17px', width: '85px', height: 37, borderColor: '#b4b4b4', color: '#e39865'}}
                >
                    {loading ?<CircularProgress size={20} style={{ color: "#e39865" }} /> : "Submit"}
                </Button>
            </form>
            <div className={styles.link}>
                <Link
                    to={`/pace-team/resourcing/resource-tracker?year=${year}&week=${week}`}
                    className={`${styles.rt} ${location.pathname === `/pace-team/resourcing/resource-tracker` && `${styles.focused}`}`}>
                        Resource Tracker
                </Link>
                {menuConfig.map(({ key, label, items, iconColor }) => (
                    <React.Fragment key={key}>
                        <div className={styles.rt} onClick={handleOpen(key)}>
                            {label} <FontAwesomeIcon icon={faChevronDown} color={iconColor} />
                        </div>
                        <Menu
                            anchorEl={anchors[key]}
                            open={Boolean(anchors[key])}
                            onClose={handleClose(key)}
                        >
                        {items.map(({ label, path, external, onClick }: any) => (
                            external ? (
                              <MenuItem key={label} onClick={handleClose(key)}>
                                <a href={path} target="_blank" rel="noopener noreferrer" className={styles.dropdown}>{label}</a>
                              </MenuItem>
                            ) : onClick ? (
                                <MenuItem onClick={() => {onClick(); handleClose(key)}}>{label}</MenuItem>
                            ) : (
                              <Link to={path} className={styles.dropdown} key={label} onClick={handleClose(key)}>
                                <MenuItem>{label}</MenuItem>
                              </Link>
                            )
                        ))}
                      </Menu>
                    </React.Fragment>
                ))}
                <Tooltip title="">
                    <IconButton onClick={handleOpen('user')}>
                        <Avatar sx={{ height: 35, width: 35, fontSize: 16, backgroundColor: avatarColor }}>
                            {getInitials(state.user?.name, state.user?.surname)}
                        </Avatar>
                    </IconButton>
                </Tooltip>
                <Menu anchorEl={anchors.user} open={Boolean(anchors.user)} onClose={handleClose('user')}>
                    <Link to='/pace-team/resourcing/my-profile' className={styles.dropdown} onClick={handleClose('user')}>
                        <MenuItem sx={{ width: 200 }}>My Profile</MenuItem>
                    </Link>
                    <Link to='/pace-team/resourcing/dashboard' className={styles.dropdown} onClick={handleClose('user')}>
                        <MenuItem sx={{ width: 200 }}>Dashboard</MenuItem>
                    </Link>
                    <MenuItem sx={{ width: 200 }} onClick={() => { handleClose('user')(); logOut(); }}>
                        Logout
                    </MenuItem>
                </Menu>
            </div>
        </div>
    )
}

export default Header;
